import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, BookOpen, Calendar } from "lucide-react";

type Section = { heading: string; paragraphs: string[]; bullets?: string[] };
type Article = {
  slug: string;
  title: string;
  description: string;
  tag: string;
  updated: string;
  intro: string;
  sections: Section[];
};

const ARTICLES: Record<string, Article> = {
  "ccma-preparation": {
    slug: "ccma-preparation",
    title: "How to prepare for a CCMA hearing",
    description:
      "A practical, step-by-step guide for South African employees and employers: how to refer a dispute, prepare your bundle, and what to expect at conciliation and arbitration.",
    tag: "Labour",
    updated: "2026",
    intro:
      "The Commission for Conciliation, Mediation and Arbitration (CCMA) is a free, independent statutory body that resolves disputes between employers and employees in South Africa. Most people walk into their first CCMA hearing with little idea what to expect — and lose ground simply because they were not prepared. This guide will fix that.",
    sections: [
      {
        heading: "1. Know your time limits",
        paragraphs: [
          "An unfair dismissal dispute must be referred within 30 days of the date of dismissal. An unfair labour practice must be referred within 90 days of the act or omission. Miss the deadline and you have to apply for condonation — which is granted only on good cause shown.",
          "Lodge the LRA Form 7.11 (Referral of a Dispute to the CCMA) as soon as possible. You can file it in person at any CCMA office, by email or by fax.",
        ],
      },
      {
        heading: "2. Understand the two stages",
        paragraphs: [
          "Almost every CCMA matter has two stages: conciliation (an informal settlement discussion led by a commissioner) and, if conciliation fails, arbitration (a formal hearing where the commissioner takes evidence and issues a binding award).",
          "Conciliation is private and off the record. Arbitration is on the record and binding. Prepare for both from day one.",
        ],
      },
      {
        heading: "3. Build your bundle",
        paragraphs: [
          "Your bundle is the set of documents the commissioner will work from. Index it, paginate it, and bring three copies (one for you, one for the other side, one for the commissioner).",
        ],
        bullets: [
          "Employment contract and any amendments",
          "Payslips for the last 3–6 months",
          "Any written warnings, charge sheets or disciplinary hearing minutes",
          "The dismissal letter or notice",
          "Relevant company policies (disciplinary code, leave policy, etc.)",
          "WhatsApp / email correspondence that supports your version",
        ],
      },
      {
        heading: "4. Prepare your witnesses",
        paragraphs: [
          "Witnesses give evidence under oath. Walk each witness through what they will be asked and remind them: tell the truth, stick to what you personally saw or did, and don't speculate.",
        ],
      },
      {
        heading: "5. On the day",
        paragraphs: [
          "Arrive 30 minutes early. Dress neatly. Bring ID, your referral form, your bundle and your witnesses. Be respectful to everyone in the room, including the commissioner and the other side — your conduct is part of the case.",
        ],
      },
      {
        heading: "When to get help",
        paragraphs: [
          "You don't need a representative at the CCMA, but a properly prepared file can be the difference between winning and losing. We prepare CCMA referrals, bundles, witness statements and arbitration outlines for clients across South Africa.",
        ],
      },
    ],
  },

  "popia-for-smes": {
    slug: "popia-for-smes",
    title: "Understanding POPIA for small businesses",
    description:
      "The Protection of Personal Information Act in plain English — what it actually requires from a small South African business, and how to get compliant without paying for an enterprise programme.",
    tag: "Compliance",
    updated: "2026",
    intro:
      "POPIA — the Protection of Personal Information Act, 4 of 2013 — has been fully in force since 1 July 2021. It applies to almost every South African business that processes personal information, including small businesses. The good news: getting a small business to a defensible POPIA baseline is not as hard as compliance vendors make it sound.",
    sections: [
      {
        heading: "What POPIA actually requires",
        paragraphs: [
          "POPIA sets out 8 conditions for lawful processing of personal information: accountability, processing limitation, purpose specification, further processing limitation, information quality, openness, security safeguards, and data subject participation.",
          "In practical terms, your business needs to know what personal information it holds, why it holds it, how long it keeps it, who else sees it, and how it protects it.",
        ],
      },
      {
        heading: "The minimum compliance pack",
        paragraphs: ["A small business in South Africa typically needs:"],
        bullets: [
          "A public-facing privacy policy on the website",
          "Consent or notice mechanisms in sign-up, intake and contact forms",
          "An internal record of processing activities (what data, why, how long, who has access)",
          "Operator agreements with any third party that processes data on your behalf (CRM, payroll, cloud storage)",
          "A registered Information Officer (by default, the head of the business)",
          "A basic data breach response procedure",
        ],
      },
      {
        heading: "Special personal information",
        paragraphs: [
          "POPIA gives stricter protection to 'special personal information' — race, health, religion, biometric data, criminal behaviour and children's data. If you process any of these, you need a specific lawful basis under section 27 or 35.",
        ],
      },
      {
        heading: "Penalties for getting it wrong",
        paragraphs: [
          "The Information Regulator can issue enforcement notices, impose administrative fines of up to R10 million and refer serious matters for criminal prosecution. Reputational damage from a breach is often worse than the fine.",
        ],
      },
      {
        heading: "Where to start",
        paragraphs: [
          "Start with a one-page data inventory: list every system that holds personal information, what fields, how long you keep it, who can access it. From there, the policy, the operator agreements and the breach procedure are straightforward. We prepare full POPIA packs for small South African businesses on a flat fee.",
        ],
      },
    ],
  },

  "employee-rights-disciplinary": {
    slug: "employee-rights-disciplinary",
    title: "Employee rights during disciplinary hearings",
    description:
      "What South African employees are entitled to under Schedule 8 of the Labour Relations Act — and how to use those rights effectively without escalating the conflict.",
    tag: "Labour",
    updated: "2026",
    intro:
      "If you've been issued a charge sheet, take a breath. South African labour law gives every employee facing discipline a clear set of procedural and substantive rights. Knowing them — and using them calmly — is the single biggest factor in a fair outcome.",
    sections: [
      {
        heading: "Your right to know the charge",
        paragraphs: [
          "You are entitled to a written charge sheet setting out the alleged misconduct in clear terms, with dates, places and a description of the conduct complained of. A vague or general charge is procedurally unfair.",
        ],
      },
      {
        heading: "Your right to reasonable notice",
        paragraphs: [
          "You should receive enough notice of the hearing to prepare your defence. What is 'reasonable' depends on the complexity of the matter, but anything less than 48 hours is usually challenged.",
        ],
      },
      {
        heading: "Your right to representation",
        paragraphs: [
          "At an internal hearing you are entitled to be represented by a fellow employee or, if your workplace recognises a union, a shop steward. External legal representation is not automatic — it depends on the employer's disciplinary code and the complexity of the matter.",
        ],
      },
      {
        heading: "Your right to a fair hearing",
        paragraphs: ["A fair hearing requires the employer to:"],
        bullets: [
          "Allow you to state your version of events",
          "Allow you to call witnesses and present documents",
          "Allow you to cross-examine the employer's witnesses",
          "Use an impartial chairperson with no prior involvement in the matter",
          "Apply the disciplinary code consistently",
        ],
      },
      {
        heading: "Your right to an outcome and reasons",
        paragraphs: [
          "After the hearing, you are entitled to a written outcome with reasons. If the sanction is dismissal, you have the right to refer the matter to the CCMA within 30 days.",
        ],
      },
      {
        heading: "How to use these rights well",
        paragraphs: [
          "Stay calm. Take notes during the hearing. Object on the record (politely) if any of your rights are denied. Don't sign anything you don't understand. And if you need help preparing your defence, get it before the hearing, not after.",
        ],
      },
    ],
  },

  "court-online-explained": {
    slug: "court-online-explained",
    title: "Court Online South Africa explained",
    description:
      "What Court Online and CaseLines are, who has to use them, and how High Court filings actually work in South Africa today — explained for clients and small firms.",
    tag: "Courts",
    updated: "2026",
    intro:
      "South Africa's High Courts have moved from paper to digital. Court Online and CaseLines now handle the issuing and filing of most matters in the Gauteng Divisions and progressively in other divisions. If you're a self-represented party or a small firm without a dedicated court clerk, the system can feel intimidating — it doesn't need to.",
    sections: [
      {
        heading: "What is Court Online?",
        paragraphs: [
          "Court Online is the Office of the Chief Justice's case management platform. It is used to issue summonses and notices of motion, to file pleadings and notices, and to manage the case record digitally. Once a case is issued, the matter is also loaded onto CaseLines.",
        ],
      },
      {
        heading: "What is CaseLines?",
        paragraphs: [
          "CaseLines is the evidence-bundling and judge-facing platform that sits alongside Court Online. The trial bundle, heads of argument, practice note and authorities are uploaded to CaseLines so the judge can read them digitally and reference page numbers in real time during the hearing.",
        ],
      },
      {
        heading: "Who has to use it?",
        paragraphs: [
          "In the Gauteng Divisions (Pretoria and Johannesburg), Court Online is mandatory for most new High Court matters. Other divisions are being phased in. Practice directives are issued by each Judge President and change from time to time — always check the current directive for the division you are filing in.",
        ],
      },
      {
        heading: "Common pitfalls",
        paragraphs: ["The most common reasons documents are rejected or hearings stumble:"],
        bullets: [
          "Bundle uploaded out of order or without a proper index",
          "Pleadings filed in the wrong sub-folder on CaseLines",
          "Heads of argument not uploaded by the deadline in the practice directive",
          "Cross-references to page numbers that don't match the CaseLines pagination",
          "Service not effected or not uploaded with the proof of service",
        ],
      },
      {
        heading: "Getting it right",
        paragraphs: [
          "If you're a small firm, the simplest fix is to delegate Court Online and CaseLines preparation to a paralegal who does this every day. We support attorneys with bundle preparation, indexing, filing and uploads on a brief-by-brief basis.",
        ],
      },
    ],
  },

  "ccma-referral-guide": {
    slug: "ccma-referral-guide",
    title: "CCMA referral guide: how to complete and file LRA Form 7.11",
    description:
      "Step-by-step CCMA referral guide for South Africa: time limits, how to complete LRA Form 7.11, proof of service, condonation and what happens after you refer.",
    tag: "Labour",
    updated: "2026",
    intro:
      "Referring a dispute to the Commission for Conciliation, Mediation and Arbitration (CCMA) starts with one document: LRA Form 7.11. It is free to file, but referrals are rejected or delayed every day for avoidable reasons — a missed deadline, the wrong dispute category, or no proof that the employer was served. This guide walks through the referral itself, from the day the dispute arises to the notice of set-down landing in your inbox.",
    sections: [
      {
        heading: "1. Check that the CCMA has jurisdiction",
        paragraphs: [
          "The CCMA deals with disputes under the Labour Relations Act 66 of 1995, the Basic Conditions of Employment Act and the Employment Equity Act. Before you refer, confirm three things: that you were an employee (not an independent contractor), that your employer is not covered by a bargaining council with jurisdiction over that sector, and that your dispute type is one the CCMA can hear.",
          "If a bargaining council covers your sector — for example MEIBC for metal and engineering or the NBCRFLI for road freight — the referral goes to that council, not the CCMA. Filing at the wrong forum wastes your time limit.",
        ],
        bullets: [
          "Unfair dismissal (misconduct, incapacity, operational requirements, constructive dismissal)",
          "Unfair labour practice (promotion, demotion, probation, benefits, training, unfair suspension)",
          "Unpaid wages, notice pay, leave pay and other BCEA money claims",
          "Unfair discrimination under the Employment Equity Act",
          "Severance pay and disputes about the interpretation of a collective agreement",
        ],
      },
      {
        heading: "2. Diarise the time limit before anything else",
        paragraphs: [
          "Unfair dismissal disputes must be referred within 30 days of the date of dismissal. Unfair labour practice disputes must be referred within 90 days of the act or omission. Discrimination disputes under the Employment Equity Act must be referred within six months.",
          "The clock runs from the date the dismissal took effect, not the date you received the letter or the date an internal appeal was decided. If you are already out of time, you must file a condonation application together with the referral — see section 6.",
        ],
      },
      {
        heading: "3. Complete LRA Form 7.11 correctly",
        paragraphs: [
          "Form 7.11 is the Referral of a Dispute to the CCMA. It is short, but every block matters because the commissioner reads it before reading anything else. Complete it in block letters, keep the summary factual, and do not argue your case on the form.",
        ],
        bullets: [
          "Part A — your full names, ID number, physical and postal addresses, cellphone number and email. This is where the notice of set-down will be sent, so check it twice.",
          "Part B — the employer's registered name (not the trading name only), physical address for service, and a contact person with a working email or fax number.",
          "Part C — the dispute category. Tick one main category. 'Unfair dismissal — misconduct' and 'unfair labour practice' are different disputes with different time limits and different outcomes.",
          "Part D — a short factual summary: what happened, on what date, and what you want. Three to six sentences is enough.",
          "Part E — the outcome you seek: reinstatement, re-employment or compensation, and the amount you say is owed.",
          "Part F — whether you need an interpreter, and whether you have a disability that requires accommodation at the hearing.",
          "Signature and date — an unsigned form is not a valid referral.",
        ],
      },
      {
        heading: "4. Serve the form on your employer and keep the proof",
        paragraphs: [
          "The CCMA does not serve the referral for you. You must serve a copy on the employer yourself and then attach proof of service to the copy you file. This is the single most common reason referrals are sent back.",
          "Acceptable proof includes a signed and dated acknowledgement of receipt, a registered post slip, a fax transmission report, an email delivery confirmation, or a sheriff's return of service. Keep the original and file a copy.",
        ],
      },
      {
        heading: "5. File the referral with the CCMA",
        paragraphs: [
          "You can file at any CCMA office in person, by email, by fax, or through the CCMA's online referral facility. Filing is free — no commissioner, official or representative may charge you to refer a dispute.",
          "You will receive a case number. Save it, and quote it in every later email, request for postponement or submission of documents.",
        ],
      },
      {
        heading: "6. If you are late: condonation",
        paragraphs: [
          "A late referral is not automatically dead, but it will not be heard until condonation is granted. File a condonation application with the referral, in affidavit form, dealing with the factors the CCMA weighs.",
        ],
        bullets: [
          "The degree of lateness — measured in days, stated honestly",
          "A full explanation covering every day of the delay, not just part of it",
          "Your prospects of success on the merits of the dispute itself",
          "The prejudice to you if condonation is refused, and to the employer if it is granted",
          "Any other relevant factor, such as illness, incorrect advice or a bargaining council referral made in good faith",
        ],
      },
      {
        heading: "7. What happens after you refer",
        paragraphs: [
          "The CCMA issues a notice of set-down for conciliation, usually within 30 days of the referral. Conciliation is informal, private and off the record; the commissioner tries to help the parties settle.",
          "If conciliation fails, the commissioner issues a certificate of outcome. You then have 90 days to refer the matter to arbitration on LRA Form 7.13 (or to refer it to the Labour Court, if the dispute type requires that). Arbitration is a formal hearing on the record, and the award is binding.",
        ],
      },
      {
        heading: "Referral checklist",
        paragraphs: [
          "Run through this list before you file. If every line is ticked, your referral will not be turned away on a technicality.",
        ],
        bullets: [
          "Correct forum confirmed (CCMA, not a bargaining council)",
          "Referral within the time limit, or condonation affidavit attached",
          "Form 7.11 fully completed, signed and dated",
          "One dispute category ticked, matching the relief you are asking for",
          "Employer's registered name and service address correct",
          "Copy served on the employer and proof of service attached",
          "Your contact details correct so the set-down notice reaches you",
          "A copy of everything kept for your own file",
        ],
      },
      {
        heading: "Next step: prepare the case, not just the form",
        paragraphs: [
          "Filing the referral is the easy part. What decides the outcome is the bundle, the witness statements and the argument at arbitration. Our CCMA preparation service covers the referral, the bundle, witness statements and an arbitration outline, prepared for you on a fixed fee.",
          "For what to do after the referral is filed, read our companion guide on how to prepare for a CCMA hearing. We provide legal information and legal advice on non-reserved matters; we do not appear in court on your behalf.",
        ],
      },
    ],
  },
};

export const Route = createFileRoute("/guides/$slug")({
  loader: ({ params }) => {
    const article = ARTICLES[params.slug];
    if (!article) throw notFound();
    return { article };
  },
  head: ({ loaderData, params }) => {
    const a = loaderData?.article;
    if (!a) return { meta: [{ title: "Guide not found" }] };
    const url = `https://nkadimengdannylcs.com/guides/${params.slug}`;
    return {
      meta: [
        { title: `${a.title} | Nkadimeng Danny Legal` },
        { name: "description", content: a.description },
        { property: "og:title", content: a.title },
        { property: "og:description", content: a.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: a.title,
            description: a.description,
            author: {
              "@type": "Person",
              name: "Nkadimeng Danny",
            },
            publisher: {
              "@type": "Organization",
              name: "Nkadimeng Danny Legal & Compliance Support",
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container-narrow py-24 text-center">
      <h1 className="font-display text-3xl text-navy">Guide not found</h1>
      <Link
        to="/guides"
        className="mt-6 inline-flex items-center gap-1 text-sm text-navy hover:text-gold"
      >
        <ArrowLeft className="h-4 w-4" /> Back to all guides
      </Link>
    </div>
  ),
  errorComponent: ({ reset }) => (
    <div className="container-narrow py-24 text-center">
      <h1 className="font-display text-3xl text-navy">This guide didn't load</h1>
      <button
        onClick={reset}
        className="mt-6 inline-flex items-center gap-1 rounded-md bg-navy px-4 py-2 text-sm text-navy-foreground"
      >
        Try again
      </button>
    </div>
  ),
  component: GuidePage,
});

function GuidePage() {
  const { article } = Route.useLoaderData() as { article: Article };

  return (
    <article className="container-narrow py-16">
      <Link
        to="/guides"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-navy"
      >
        <ArrowLeft className="h-4 w-4" /> All guides
      </Link>

      <header className="mt-6">
        <p className="text-xs uppercase tracking-[0.25em] text-gold">
          {article.tag}
        </p>
        <h1 className="mt-2 font-display text-4xl text-navy md:text-5xl">
          {article.title}
        </h1>
        <p className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
          <Calendar className="h-3.5 w-3.5" /> Updated {article.updated}
        </p>
        <p className="mt-6 max-w-3xl text-lg text-muted-foreground leading-relaxed">
          {article.intro}
        </p>
      </header>

      <div className="mt-12 space-y-10 max-w-3xl">
        {article.sections.map((s) => (
          <section key={s.heading}>
            <h2 className="font-display text-2xl text-navy">{s.heading}</h2>
            {s.paragraphs.map((p, i) => (
              <p key={i} className="mt-3 text-foreground/85 leading-relaxed">
                {p}
              </p>
            ))}
            {s.bullets && (
              <ul className="mt-4 space-y-2 text-foreground/85">
                {s.bullets.map((b) => (
                  <li key={b} className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                    <span className="leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      <div className="mt-14 max-w-3xl rounded-2xl border-l-4 border-gold bg-secondary/60 p-6 md:p-8">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-gold">
          <BookOpen className="h-4 w-4" /> General information only
        </div>
        <p className="mt-2 text-sm text-muted-foreground md:text-base">
          This guide is general information, not legal advice, and does not
          create an attorney-client relationship. For help with your matter,
          book a consultation with us or speak to an admitted attorney.
        </p>
        <Link
          to="/book"
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground hover:bg-navy/90"
        >
          Book a consultation <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </article>
  );
}
